import { useEffect, useId, useState } from 'react';
import styled from "@emotion/styled";
import { colors as themeColors } from 'src/theme';
import { Grid } from 'src/components';
import { mediaQueryMax, ValidationErrorStyle } from 'src/common';
import { errorIdFor, focusRing, visuallyHidden, onEnterKey } from 'src/common/style/a11y.style';

const Fieldset = styled.fieldset({
  border: 0,
  margin: 0,
  padding: 0,
  minWidth: 0,
});

const Legend = styled.legend({
  color: themeColors.text1,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  marginBottom: '8px',
  padding: 0,
});

const OptionalStyle = styled.span({
  color: themeColors.text4
});

const HiddenInput = styled.input({
  ...visuallyHidden,
  '&:focus-visible + span': focusRing,
});

const Option = styled.span((props: { 
  isSelected: boolean, 
  center: boolean, 
  colors: { background?: string, text?: string, backgroundSelected?: string, textSelected?: string },
  horizontal: boolean
 }) => ({
  display: props.horizontal ? 'flex' : 'block',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  boxSizing: 'border-box' as const,
  color: props.isSelected ? props.colors.textSelected : props.colors.text,
  backgroundColor: props.isSelected ? props.colors.backgroundSelected : props.colors.background,
  cursor: 'pointer',
  padding: props.horizontal ? '10px 16px' : '10px 24px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: props.isSelected ? '700' : '400',
  textAlign: (props.center ? 'center' : 'left') as 'center' | 'left',
  lineHeight: '18px',
  
  [mediaQueryMax('mobile')]: {
    padding: '10px 24px',
    fontSize: '18px',
    lineHeight: '20px',
    textAlign: 'left' as 'left',
    justifyContent: 'flex-start',
  },
}));

const OptionLabel = styled.label({
  display: 'block',
  position: 'relative',
  flex: 1,
  zIndex: 1,
});

const GridStyle = styled(Grid)({
  [mediaQueryMax('mobile')]: {
    gridTemplateColumns: '1fr',
  },
});

interface RadioOption {
  value: string;
  label: string;
}

interface RadioButtonGroupProps {
  id?: string;
  options: RadioOption[];
  value?: string;
  onChange?: (selectedOption: string) => void;
  className?: string;
  label?: string;
  error?: string;
  horizontal?: boolean;
  gridColumns?: string; // grid columns ima smisla samo ako je horizontal false
  center?: boolean;
  colors?: {
    background?: string;
    text?: string;
    backgroundSelected?: string;
    textSelected?: string;
  },
  optional?: boolean;
}

export const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  id,
  options,
  value,
  onChange,
  className,
  label,
  error,
  gridColumns = `1fr`,
  horizontal = false,
  center = false,
  colors: customColors = {},
  optional = false
}) => {
  const colors = {
    background: themeColors.background6,
    text: themeColors.text4,
    backgroundSelected: themeColors.primary1,
    textSelected: themeColors.text7,
    ...customColors
  };

  const [selectedOption, setSelectedOption] = useState<string | undefined>(value);
  const generatedId = useId();
  const groupName = id || generatedId;
  const errorId = errorIdFor(groupName);

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  // Funkcija za ažuriranje stanja
  const handleOptionChange = (optionValue: string) => {
    setSelectedOption(optionValue);
    if (onChange) onChange(optionValue); // Pozivamo onChange prop funkciju sa izabranom opcijom
  };

  const columns = horizontal ? `repeat(${options.length}, 1fr)` : gridColumns;

  return (
    <Fieldset aria-describedby={error ? errorId : undefined}>
      {label ? (
        <Legend>
          {label}
          {optional ? <OptionalStyle> (nije obavezno)</OptionalStyle> : null}
        </Legend>
      ) : null}
      <GridStyle columns={columns} gap='4px' className={className}>
        {options.map((option) => (
          <OptionLabel key={option.value}>
            <HiddenInput
              type="radio"
              name={groupName}
              value={option.value}
              checked={selectedOption === option.value}
              onChange={() => handleOptionChange(option.value)}
              onKeyDown={onEnterKey(() => handleOptionChange(option.value))}
            />
            <Option
              isSelected={selectedOption === option.value}
              center={center}
              colors={colors}
              horizontal={horizontal}
            >
              {option.label}
            </Option>
          </OptionLabel>
        ))}
      </GridStyle>
      {error ? <ValidationErrorStyle id={errorId}>{error}</ValidationErrorStyle> : null}
    </Fieldset>
  );
};
